import useTranslation from "next-translate/useTranslation"
import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Card, CardBody, CardHeader, Collapse, Spinner } from "reactstrap"

import { IChallenge, IFundApplication, IProject, IProjectMembership, IProposal, MembershipRole, ProjectState } from "@api/schema"
import ActionIcon from "@components/common/ActionIcon"
import FAQPopup from "@components/common/faq/FAQPopup"
import Icon from "@components/common/Icon"
import TranslatedHtml from "@components/common/TranslatedHtml"
import ConfirmModal from "@components/project/common/ConfirmModal"
import { IFormikActions, loadModelAction } from "@redux/helper/actions"
import { AppState } from "@redux/reducer"
import { selectById, selectSingleEntityUsecaseState } from "@redux/reducer/data"
import { EntityType } from "@redux/reduxTypes"
import { coordinatorCount, plannerCount } from "@services/projectHelper"
import { proposalStateAsTranslationKey } from "@services/proposalHelper"
import { idFromIModelOrIRI, uriHashMatchesId } from "@services/util"
import { MODULE_CHALLENGE_AVAILABLE, MODULE_FUNDS_AVAILABLE } from "config"

import ProjectCreatorText from "./ProjectCreatorText"
import ProjectImplementationStateIcon from "./ProjectImplementationStateIcon"
import ProjectNameText, { projectNameText } from "./ProjectNameText"
import ProgressActionIcons from "./workflow/ProgressActionIcons"
import ProjectWorkflow from "./workflow/ProjectWorkflow"


interface IProps {
  addNotification: (message: string) => void
  deleteMembership: (...args: any) => void
  updateProject: (data: IProject, actions: IFormikActions) => void
  deleteProject: (data: IProject, actions: IFormikActions) => void
  isSingle?: boolean
  membership: IProjectMembership
  project: IProject
  activeProposal?: IProposal | IFundApplication
}


/**
 * This component provides a card of one of the projects of the user, including the project-workflow.
 * Those cards are used at the "my projects"-dashboard-page.
 *
 * @todo check, if last project coordinator may leave the project, or if he has to transfer his role first
 */
const MyProjectCard: React.FC<IProps> = ({
  deleteMembership,
  isSingle = false,
  membership,
  project,
  updateProject,
  deleteProject,
  activeProposal,
  addNotification }: IProps) => {
  const { t } = useTranslation("common")

  const hashId = `project-${project.id.toString()}`

  const dispatch = useDispatch()

  // only get the active challenge, if the module is active
  const challengeId = (MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) && idFromIModelOrIRI(activeProposal?.challenge)
  const activeChallenge = useSelector((state: AppState) => (MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) && challengeId &&
    selectById<IChallenge>(state, EntityType.Challenge, challengeId))

  const challengeRequest = useSelector((state: AppState) => (MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) && challengeId &&
    selectSingleEntityUsecaseState(state, EntityType.Challenge, challengeId.toString()))

  const [isOpen, setIsOpen] = useState(false)
  const toggle = () => { setIsOpen(!isOpen) }

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const toggleDeleteModal = () => setDeleteModalOpen(!deleteModalOpen)

  const onDeleteConfirm = (_values: IProject, actions: IFormikActions) => {
    actions.onSuccess = () => {
      toggleDeleteModal()
      addNotification("message.project.deleted")
    }
    deleteProject(project, actions)
  }

  const [deleteMembershipModalOpen, setDeleteMembershipModalOpen] = useState(false)
  const toggleDeleteMembershipModal = () => setDeleteMembershipModalOpen(!deleteMembershipModalOpen)

  const [deleteApplicationModalOpen, setDeleteApplicationModalOpen] = useState(false)
  const toggleDeleteApplicationModal = () => setDeleteApplicationModalOpen(!deleteApplicationModalOpen)

  const onDeleteMembershipConfirm = (_values: any, actions: IFormikActions) => {
    actions.onSuccess = () => {
      // remove both modals with delete-membership-context
      setDeleteMembershipModalOpen(false)
      setDeleteApplicationModalOpen(false)
    }
    deleteMembership(membership, actions)
  }

  // following data is important for the modal of leaving a project
  const isOnlyCoordinator = coordinatorCount(project) <= 1 && membership?.role === MembershipRole.Coordinator
  const hasPotentialCoordinators = plannerCount(project) > 0


  useEffect(() => {
    setIsOpen(!project?.locked && (isSingle || uriHashMatchesId(hashId)))
  }, [])

  useEffect(() => {
    // only load the challenge, if the module is active
    if ((MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) && challengeId && !challengeRequest?.loaded) {
      dispatch(loadModelAction(EntityType.Challenge, challengeId))
    }
  }, [challengeRequest?.loaded])

  // failsave
  if (!membership || project.deletedAt) {
    return null
  }

  /**
   * show the projectcard under following conditions:
   * 1) module challenge or module fund is active and the challenge has been loaded for the proposal
   * 2) module challenge or module fund is active, but the project has no active proposal
   * 3) module challenge or module fund is not active
   */
  return ((((MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) && challengeRequest?.loaded)
    || ((MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE) && !activeProposal)
    || (!(MODULE_CHALLENGE_AVAILABLE || MODULE_FUNDS_AVAILABLE))) ?
    <Card role="my project info" className="collapsable-card">
      <CardHeader
        aria-expanded={isOpen}
        id={hashId}
        onClick={toggle}
        onKeyUp={(event) => ((event.key === "Enter") || (event.code === "Space")) && toggle()}  // open the card on Enter
        tabIndex={0}
      >
        <div className="title-section">
          <h3
            className={project.state === ProjectState.Deactivated
              || project.state === ProjectState.Inactive
              || project.locked
              || membership.role === MembershipRole.Applicant ? "text-muted" : ""}
            id="cardtitle">
            <ProjectNameText project={project} />
            {project.locked
              ? <small className="text-danger">&emsp; {t("project.state.locked")}</small>
              : <>&emsp;
                <div className="project-implementation-state"><ProjectImplementationStateIcon project={project} />
                  <small>{t("project.implementationState." + project.implementationState)}</small></div>
                {project.state === ProjectState.Deactivated && <small> &ndash; {t("project.state.deactivated")}</small>}
                {project.state === ProjectState.Inactive && <small> &ndash; {t("project.state.inactive")}</small>}
                {
                  project.state !== ProjectState.Active && <FAQPopup faqKey="introduction.projects.progress" />
                }
              </>
            }
            { // allow to delete the project, when the user is the only user in the project, he is coordinator
              // and the project is deactivated
              project.state === ProjectState.Deactivated
              && membership.role === MembershipRole.Coordinator
              && project.memberCount === 1 &&
              <>
                <ActionIcon
                  aria-label={t("project.delete")}
                  onclick={toggleDeleteModal}
                  title={t("project.delete")}
                  icon="trash"
                />
                <ConfirmModal
                  title={projectNameText(project, t)}
                  content="form.project.delete"
                  onSubmit={onDeleteConfirm}
                  modalOpen={deleteModalOpen}
                  toggle={toggleDeleteModal}
                />
              </>
            }
          </h3>
          <div className="project-information">
            <ProjectCreatorText project={project} />
            &emsp;{t("common:project.updatedAt")}: {t("common:default.dateTime", { value: project.updatedAt })}&emsp;{
              activeChallenge && (t("proposal.challenge", { challengeName: activeChallenge.name }))}
            &emsp;{ // display proposal state if existing
              activeProposal && <>[{t("proposal.proposal") + ": " + t(proposalStateAsTranslationKey(activeProposal, activeChallenge))}]</>}
          </div>
        </div>
        <span className={"chevron-right" + (isOpen ? " chevron-right--toggled" : "")}><Icon name="chevron-right" size={42} /></span>
      </CardHeader>
      <Collapse isOpen={isOpen}>
        <CardBody>
          {membership.role === MembershipRole.Applicant
            ? <>
              <TranslatedHtml content="page.user.projects.applicationOpen" />
              <ActionIcon
                aria-label={t("page.user.projects.deleteMyApplication")}
                onclick={toggleDeleteApplicationModal}
                title={t("page.user.projects.deleteMyApplication")}
                icon="trash"
              />
              <ConfirmModal
                title={t("page.user.projects.deleteMyApplication")}
                content="message.project.confirmDelete.myApplication"
                onSubmit={onDeleteMembershipConfirm}
                modalOpen={deleteApplicationModalOpen}
                toggle={toggleDeleteApplicationModal}
              />
            </>
            : <>
              {project.locked
                ? <><TranslatedHtml content="page.user.projects.projectIsLocked" />
                  <ActionIcon
                    aria-label={t("page.projects.members.leaveProject")}
                    title={t("page.projects.members.leaveProject")}
                    icon="trash"
                    onclick={toggleDeleteMembershipModal}
                  />
                  <ConfirmModal
                    title={t("page.projects.members.leaveProjectHeading")}
                    content={isOnlyCoordinator && hasPotentialCoordinators ? "page.projects.members.cannotLeaveAsSingleCoordinator" : "page.projects.members.leaveProjectIntro"}
                    submitButtonDisabled={isOnlyCoordinator && hasPotentialCoordinators}
                    onSubmit={onDeleteMembershipConfirm}
                    modalOpen={deleteMembershipModalOpen}
                    toggle={toggleDeleteMembershipModal}
                  />
                </>
                : <>
                  <ProgressActionIcons // icons on the upper right on every project card
                    membership={membership}
                    project={project}
                    updateProject={updateProject}
                  />
                  <ProjectWorkflow // Workflow
                    project={project}
                  />
                </>
              }
            </>
          }
        </CardBody>
      </Collapse>
    </Card>
    : <Spinner />
  )
}

export default MyProjectCard
